import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, Check, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import HisobAppPreview from '@/components/marketing/HisobAppPreview';
import { MediaUploadField } from '@/components/media/MediaUploadField';
import {
  useCashbackOffers, useEligibleCategories, useMarketingMerchants, useMarketingMutation,
} from '@/hooks/use-marketing';
import {
  PROMOTION_TYPES, bpsToPercent, formatPeriod, promotionLabel, type PromotionType,
} from '@/lib/marketing';

interface DraftItem {
  key: string;
  merchant_id: string;
  merchant_name: string;
  category_id?: string | null;
  category_name?: string | null;
  cashback_offer_id?: string | null;
  cashback_bps?: number | null;
  promo_title: string;
  promo_subtitle: string;
  promo_image_url: string;
  is_active: boolean;
}

export default function AdminMarketingCampaignNew() {
  const { t } = useTranslation();
  const STEPS = ['stepBasic', 'stepPeriod', 'stepItems', 'stepMerch', 'stepReview'].map((k) => t(`mkt_campaign_new.${k}`));
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({
    name: '',
    promotion_type: 'VIP_STORES' as PromotionType,
    description: '',
    start_at: '',
    end_at: '',
  });
  const [items, setItems] = useState<DraftItem[]>([]);
  const [pickMerchant, setPickMerchant] = useState('');
  const [pickCategory, setPickCategory] = useState('');
  const [pickOffer, setPickOffer] = useState('');

  const { data: merchants = [] } = useMarketingMerchants();
  const { data: offers = [] } = useCashbackOffers({ adminView: true });
  const { data: categories = [] } = useEligibleCategories(pickMerchant || undefined, { adminView: true });
  const create = useMarketingMutation('create_campaign');

  const approvedOffers = useMemo(
    () => (offers as any[]).filter((o) => ['APPROVED', 'ACTIVE'].includes(o.status)),
    [offers],
  );

  const isCategories = form.promotion_type === 'PROMOTED_CATEGORIES';
  const isCashback = form.promotion_type === 'BIG_CASHBACK';

  const addItem = () => {
    if (isCashback) {
      const offer = approvedOffers.find((o) => String(o.id) === pickOffer);
      if (!offer) return toast.error(t('mkt_campaign_new.selectOffer'));
      if (items.some((i) => i.cashback_offer_id === offer.id)) return toast.error(t('mkt_campaign_new.offerDuplicate'));
      setItems([...items, {
        key: crypto.randomUUID(),
        merchant_id: offer.merchant_id,
        merchant_name: offer.merchant?.name ?? '—',
        cashback_offer_id: offer.id,
        cashback_bps: offer.cashback_bps,
        promo_title: '', promo_subtitle: '', promo_image_url: '', is_active: true,
      }]);
      setPickOffer('');
      return;
    }

    const merchant = (merchants as any[]).find((m) => String(m.id) === pickMerchant);
    if (!merchant) return toast.error(t('mkt_campaign_new.selectStore'));

    if (isCategories) {
      if (!pickCategory) return toast.error(t('mkt_campaign_new.selectCategory'));
      const cat = categories.find((c) => String(c.id) === pickCategory);
      if (items.some((i) => i.merchant_id === merchant.id && i.category_id === pickCategory)) {
        return toast.error(t('mkt_campaign_new.pairDuplicate'));
      }
      setItems([...items, {
        key: crypto.randomUUID(),
        merchant_id: merchant.id,
        merchant_name: merchant.name,
        category_id: pickCategory,
        category_name: cat?.name ?? null,
        promo_title: '', promo_subtitle: '', promo_image_url: '', is_active: true,
      }]);
      setPickCategory('');
      return;
    }

    if (items.some((i) => i.merchant_id === merchant.id)) return toast.error(t('mkt_campaign_new.storeDuplicate'));
    setItems([...items, {
      key: crypto.randomUUID(),
      merchant_id: merchant.id,
      merchant_name: merchant.name,
      promo_title: '', promo_subtitle: '', promo_image_url: '', is_active: true,
    }]);
    setPickMerchant('');
  };

  const patchItem = (key: string, patch: Partial<DraftItem>) =>
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, ...patch } : i)));

  const move = (index: number, dir: -1 | 1) => {
    const target = index + dir;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
  };

  const canNext = () => {
    if (step === 0) return form.name.trim().length > 0;
    if (step === 1) return !!form.start_at && !!form.end_at && new Date(form.start_at) < new Date(form.end_at);
    if (step === 2) return items.length > 0;
    return true;
  };

  const save = async (schedule: boolean) => {
    if (!canNext()) return;
    try {
      // FR-97: one atomic call — an invalid participant rolls the whole campaign back.
      const created: any = await create.mutateAsync({
        name: form.name,
        promotion_type: form.promotion_type,
        description: form.description,
        start_at: new Date(form.start_at).toISOString(),
        end_at: new Date(form.end_at).toISOString(),
        status: schedule ? 'SCHEDULED' : 'DRAFT',
        items: items.map((i, idx) => ({
          merchant_id: Number(i.merchant_id),
          category_id: i.category_id ? Number(i.category_id) : undefined,
          cashback_offer_id: i.cashback_offer_id ? Number(i.cashback_offer_id) : undefined,
          display_order: idx + 1,
          promo_title: i.promo_title,
          promo_subtitle: i.promo_subtitle,
          promo_image_url: i.promo_image_url,
          is_active: i.is_active,
        })),
      });
      const campaignId = created?.data?.id;
      if (!campaignId) throw new Error(t('mkt_campaign_new.createFailed'));

      toast.success(schedule ? t('mkt_campaign_new.scheduled') : t('mkt_campaign_new.draftSaved'));
      navigate(`/admin/marketing/campaigns/${campaignId}`);
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  return (
    <div className="mx-auto max-w-6xl animate-fade-in p-6">
      <button onClick={() => navigate('/admin/marketing/campaigns')} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft size={14} /> {t('mkt_campaign_new.back')}
      </button>

      <h1 className="mb-1 text-xl font-semibold text-foreground">{t('mkt_campaign_new.title')}</h1>
      <p className="mb-5 text-sm text-muted-foreground">{t('mkt_campaign_new.subtitle', { n: STEPS.length })}</p>

      <div className="mb-6 flex flex-wrap gap-2">
        {STEPS.map((s, i) => (
          <div key={s} className={`flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs ${i === step ? 'border-primary bg-primary/10 text-foreground' : i < step ? 'border-border bg-muted text-muted-foreground' : 'border-border text-muted-foreground'}`}>
            {i < step ? <Check size={12} /> : <span className="font-semibold">{i + 1}</span>}
            {s}
          </div>
        ))}
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_320px]">
        <div className="rounded-xl border border-border bg-card p-5">
          {step === 0 && (
            <div className="space-y-4">
              <div>
                <Label>{t('mkt_campaign_new.name')}</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder={t('mkt_campaign_new.namePlaceholder')} />
              </div>
              <div>
                <Label>{t('mkt_campaign_new.promoType')}</Label>
                <Select value={form.promotion_type} onValueChange={(v) => { setForm({ ...form, promotion_type: v as PromotionType }); setItems([]); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PROMOTION_TYPES.map((pt) => <SelectItem key={pt} value={pt}>{promotionLabel[pt]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>{t('mkt_campaign_new.internalComment')}</Label>
                <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>{t('mkt_campaign_new.start')}</Label>
                <Input type="datetime-local" value={form.start_at} onChange={(e) => setForm({ ...form, start_at: e.target.value })} />
              </div>
              <div>
                <Label>{t('mkt_campaign_new.end')}</Label>
                <Input type="datetime-local" value={form.end_at} onChange={(e) => setForm({ ...form, end_at: e.target.value })} />
              </div>
              {form.start_at && form.end_at && new Date(form.start_at) >= new Date(form.end_at) && (
                <p className="text-sm text-destructive sm:col-span-2">{t('mkt_campaign_new.dateError')}</p>
              )}
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4">
              {isCashback ? (
                <div>
                  <Label>{t('mkt_campaign_new.approvedOffer')}</Label>
                  <Select value={pickOffer || '__none__'} onValueChange={(v) => setPickOffer(v === '__none__' ? '' : v)}>
                    <SelectTrigger><SelectValue placeholder={t('mkt_campaign_new.selectOfferPlaceholder')} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="__none__">{t('mkt_campaign_new.notSelected')}</SelectItem>
                      {approvedOffers.map((o: any) => (
                        <SelectItem key={o.id} value={String(o.id)}>
                          {o.merchant?.name} — {bpsToPercent(o.cashback_bps)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {approvedOffers.length === 0 && (
                    <p className="mt-2 text-xs text-muted-foreground">{t('mkt_campaign_new.noApprovedOffers')}</p>
                  )}
                </div>
              ) : (
                <>
                  <div>
                    <Label>{t('mkt_campaign_new.store')}</Label>
                    <Select value={pickMerchant || '__none__'} onValueChange={(v) => { setPickMerchant(v === '__none__' ? '' : v); setPickCategory(''); }}>
                      <SelectTrigger><SelectValue placeholder={t('mkt_campaign_new.selectStore')} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">{t('mkt_campaign_new.notSelected')}</SelectItem>
                        {(merchants as any[]).map((m) => <SelectItem key={m.id} value={String(m.id)}>{m.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {isCategories && (
                    <div>
                      <Label>{t('mkt_campaign_new.categoryFromProducts')}</Label>
                      <Select value={pickCategory || '__none__'} onValueChange={(v) => setPickCategory(v === '__none__' ? '' : v)}>
                        <SelectTrigger><SelectValue placeholder={t('mkt_campaign_new.selectCategory')} /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__none__">{t('mkt_campaign_new.notSelected')}</SelectItem>
                          {categories.map((c) => <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {pickMerchant && categories.length === 0 && (
                        <p className="mt-2 text-xs text-muted-foreground">{t('mkt_campaign_new.noCategories')}</p>
                      )}
                    </div>
                  )}
                </>
              )}
              <Button size="sm" variant="outline" onClick={addItem}><Plus size={14} className="mr-1" /> {t('mkt_campaign_new.add')}</Button>

              <div className="divide-y divide-border rounded-lg border border-border">
                {items.length === 0 ? (
                  <p className="p-6 text-center text-sm text-muted-foreground">{t('mkt_campaign_new.noItems')}</p>
                ) : items.map((it, i) => (
                  <div key={it.key} className="flex items-center justify-between gap-3 p-3">
                    <div>
                      <p className="text-sm font-medium text-foreground">{i + 1}. {it.merchant_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {it.category_name ? t('mkt_campaign_new.categoryLine', { name: it.category_name }) : null}
                        {it.cashback_bps ? t('mkt_campaign_new.cashbackLine', { value: bpsToPercent(it.cashback_bps) }) : null}
                      </p>
                    </div>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" onClick={() => move(i, -1)}>↑</Button>
                      <Button size="icon" variant="ghost" onClick={() => move(i, 1)}>↓</Button>
                      <Button size="icon" variant="ghost" onClick={() => setItems(items.filter((x) => x.key !== it.key))}>
                        <Trash2 size={14} className="text-destructive" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-4">
              {items.map((it, i) => (
                <div key={it.key} className="rounded-lg border border-border p-4">
                  <div className="mb-3 flex items-center justify-between">
                    <p className="text-sm font-medium text-foreground">
                      {i + 1}. {it.merchant_name}{it.category_name ? ` · ${it.category_name}` : ''}
                    </p>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      {t('mkt_campaign_new.active')}
                      <Switch checked={it.is_active} onCheckedChange={(v) => patchItem(it.key, { is_active: v })} />
                    </div>
                  </div>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <div>
                      <Label>{t('mkt_campaign_new.promoTitle')}</Label>
                      <Input value={it.promo_title} onChange={(e) => patchItem(it.key, { promo_title: e.target.value })} />
                    </div>
                    <div>
                      <Label>{t('mkt_campaign_new.promoSubtitle')}</Label>
                      <Input value={it.promo_subtitle} onChange={(e) => patchItem(it.key, { promo_subtitle: e.target.value })} />
                    </div>
                    <div className="sm:col-span-2">
                      <MediaUploadField
                        kind="marketing_image"
                        targetId={it.merchant_id}
                        refId={it.key}
                        value={it.promo_image_url}
                        onChange={(url) => patchItem(it.key, { promo_image_url: url })}
                        label={t('mkt_campaign_new.promoImage')}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {step === 4 && (
            <div className="space-y-4 text-sm">
              <div className="rounded-lg border border-border p-4">
                <p className="font-medium text-foreground">{form.name}</p>
                <p className="text-muted-foreground">
                  {promotionLabel[form.promotion_type]} · {formatPeriod(form.start_at, form.end_at)}
                </p>
                {form.description && <p className="mt-2 text-muted-foreground">{form.description}</p>}
              </div>
              <div className="divide-y divide-border rounded-lg border border-border">
                {items.map((it, i) => (
                  <div key={it.key} className="flex items-center justify-between gap-3 p-3">
                    <div>
                      <p className="font-medium text-foreground">{i + 1}. {it.promo_title || it.merchant_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {it.merchant_name}
                        {it.category_name ? ` · ${it.category_name}` : ''}
                        {it.cashback_bps ? ` · ${bpsToPercent(it.cashback_bps)}` : ''}
                      </p>
                    </div>
                    <Badge variant={it.is_active ? 'default' : 'outline'}>{it.is_active ? t('mkt_campaign_new.active') : t('mkt_campaign_new.inactive')}</Badge>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
            <Button variant="outline" disabled={step === 0} onClick={() => setStep(step - 1)}>
              <ArrowLeft size={14} className="mr-1" /> {t('mkt_campaign_new.prev')}
            </Button>
            {step < STEPS.length - 1 ? (
              <Button disabled={!canNext()} onClick={() => setStep(step + 1)}>
                {t('mkt_campaign_new.next')} <ArrowRight size={14} className="ml-1" />
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" disabled={create.isPending} onClick={() => save(false)}>{t('mkt_campaign_new.saveDraft')}</Button>
                <Button disabled={create.isPending} onClick={() => save(true)}>{t('mkt_campaign_new.schedule')}</Button>
              </div>
            )}
          </div>
        </div>

        <HisobAppPreview
          promotionType={form.promotion_type}
          items={items.filter((i) => i.is_active).map((i) => ({
            id: i.key,
            merchant_name: i.merchant_name,
            category_name: i.category_name,
            promo_title: i.promo_title,
            promo_subtitle: i.promo_subtitle,
            promo_image_url: i.promo_image_url,
            cashback_bps: i.cashback_bps,
          }))}
        />
      </div>
    </div>
  );
}
